import { useEffect } from "react";
import { createDialog as createDialogImpl, DialogOptions, DialogRenderComponentProps, Mixin, useScrollableMixin } from "brew-js-react";
import { classNames } from "zeta-dom-react";
import { Icon } from "src/components/common";
import { Button } from "src/components/controls";

let closeSnackbar: (() => void) | undefined;

export interface AlertDialogProps {
    title: string;
    description: string;
    image?: string;
    preventNavigation?: boolean;
    confirmText: string;
    confirmButtonVariant?: 'primary' | 'outlined';
    onCommit?: (value?: boolean) => void;
}

export interface ConfirmDialogProps {
    title: string;
    description: string;
    image?: string;
    preventNavigation?: boolean;
    confirmText: string;
    cancelText?: string;
    onCommit?: (value?: boolean) => void;
    confirmButtonVariant?: 'primary' | 'outlined';
    cancelButtonVariant?: 'outlined' | 'link';
}

function DialogWrapper(props: React.PropsWithChildren<DialogRenderComponentProps<any>>) {
    const scrollable = useScrollableMixin();
    return (
        <div {...Mixin.use(scrollable, classNames('eac-dialog-body'))}>
            {props.showCloseButton !== false &&
                <div className="eac-dialog-close" onClick={() => props.closeDialog()}><Icon icon="close" /></div>}
            <div {...Mixin.use(scrollable.target)}>
                {props.title !== undefined &&
                    <div className="eac-dialog-title">{props.title}</div>}
                <div className="eac-dialog-content eac-text">
                    {props.children}
                </div>
            </div>
        </div>
    );
}

function DialogPromptContent(props: React.PropsWithChildren<{ title: string, description: string, image?: string }>) {
    return (
        <div className="eac-dialog-prompt-content eac-text">
            {props.image &&
                <img src={props.image} alt="" />}
            <h3>{props.title}</h3>
            <p dangerouslySetInnerHTML={{ __html: props.description }}></p>
            {props.children}
        </div>
    );
}

export function createDialog<T = any>(props: DialogOptions<T>) {
    return createDialogImpl<T>({
        ...props,
        wrapper: DialogWrapper,
        className: classNames('eac-dialog', props.className)
    });
}

export function createAlertDialog(props: AlertDialogProps) {
    return createDialog({
        modal: true,
        preventNavigation: props.preventNavigation,
        showCloseButton: false,
        onCommit: props.onCommit,
        onRender({ closeDialog }) {
            return (
                <DialogPromptContent title={props.title} description={props.description} image={props.image}>
                    <Button label={props.confirmText} variant={props.confirmButtonVariant || 'outlined'} onClick={() => closeDialog(true)} />
                </DialogPromptContent>
            );
        }
    });
}

export function createConfirmDialog(props: ConfirmDialogProps) {
    return createDialog({
        modal: true,
        preventNavigation: props.preventNavigation,
        showCloseButton: false,
        onCommit: props.onCommit,
        onRender({ closeDialog }) {
            return (
                <DialogPromptContent title={props.title} description={props.description} image={props.image}>
                    <Button label={props.confirmText} variant={props.confirmButtonVariant || 'outlined'} onClick={() => closeDialog(true)} />
                    {props.cancelText &&
                        <Button label={props.cancelText} variant={props.cancelButtonVariant || 'link'} onClick={() => closeDialog(false)} />}
                </DialogPromptContent>
            );
        }
    });
}

export function openDialog<T = any>(props: DialogOptions<T>) {
    return createDialog(props).open();
}

export function openAlertDialog(props: AlertDialogProps) {
    return createAlertDialog(props).open();
}

export function openConfirmDialog(props: ConfirmDialogProps) {
    return createConfirmDialog(props).open();
}

export function showSnackbar(message: string, closeAfter = 5000, className?: string) {
    if (closeSnackbar) {
        closeSnackbar();
    }
    openDialog({
        className: classNames('eac-dialog-snackbar', className),
        showCloseButton: false,
        onRender: function Content({ closeDialog }) {
            useEffect(() => {
                setTimeout(closeDialog, closeAfter);
            }, [closeDialog]);
            closeSnackbar = closeDialog;
            return <><span>{message}</span><Icon icon="close" onClick={closeDialog} /></>;
        }
    });
}
